import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  LogOut, Users, GraduationCap, TrendingUp, Bell, MapPin, BarChart3, 
  DollarSign, ClipboardCheck, Brain, MessageSquare, Package, CalendarDays, Star 
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import CenterManagement from '../components/CenterManagement';
import UserManagement from '../components/UserManagement';
import Reports from '../components/Reports';
import Notifications from '../components/Notifications';
import Analytics from '../components/Analytics';
import KitRequestManagement from '../components/KitRequestManagement';
import FinanceManagement from '../components/FinanceManagement';
import ApprovalsManagement from '../components/ApprovalsManagement';
import AdminGameLeaderboard from '../components/AdminGameLeaderboard';
import MessagingSystem from '../components/MessagingSystem';
import ScheduleManagement from '../components/ScheduleManagement';
import AIAnalyticsDashboard from '../components/AIAnalyticsDashboard';
import FOCRequests from '../components/FOCRequests';
import StudentProgress from '../components/StudentProgress';
import { Card } from '../components/ui/card';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalCenters: 0,
    totalTeachers: 0,
    totalStudents: 0,
    totalTasks: 0
  });
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [kitRequestStats, setKitRequestStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    loadStats();
    loadNotifications();
    loadAnalyticsOverview();
    loadKitRequestStats();
    loadUnreadMessageCount();
    
    // Poll for new messages every 30 seconds
    const messageInterval = setInterval(() => {
      loadUnreadMessageCount();
    }, 30000);
    
    return () => clearInterval(messageInterval);
  }, []);

  const loadStats = async () => {
    try {
      const [centers, teachers, students] = await Promise.all([
        axios.get(`${API}/centers`),
        axios.get(`${API}/users?role=teacher`),
        axios.get(`${API}/users?role=student`)
      ]);
      setStats({
        totalCenters: centers.data.length,
        totalTeachers: teachers.data.length,
        totalStudents: students.data.length,
        totalTasks: 0
      });
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const loadAnalyticsOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyticsOverview(response.data);
    } catch (error) {
      console.error('Failed to load analytics overview', error);
    }
  };

  const loadKitRequestStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/kit-requests/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKitRequestStats(response.data);
    } catch (error) {
      console.error('Failed to load kit request stats', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications', error);
    }
  };

  const loadUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessageCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to load unread message count', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'ai-analytics', label: 'AI Analytics', icon: Brain },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: unreadMessageCount > 0 ? unreadMessageCount : null },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'games', label: 'Games', icon: Star },
    { id: 'approvals', label: 'Approvals', icon: ClipboardCheck },
    { id: 'finance', label: 'Finance', icon: DollarSign },
    { id: 'kit', label: 'Kit Requests', icon: Package },
    { id: 'centers', label: 'Centers', icon: MapPin },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'reports', label: 'Reports', icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <Sidebar
        menuItems={menuItems}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        brandColor="blue"
        header={(isOpen) => (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <img 
                src="/logo.svg" 
                alt="GeniusKid" 
                className="w-10 h-10 object-contain"
              />
            </div>
            {isOpen && (
              <div>
                <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  GeniusKid
                </h1>
                <p className="text-xs text-gray-400">Admin Panel</p>
              </div>
            )}
          </div>
        )}
        footer={(isOpen) => (
          <div className="space-y-2">
            <button
              onClick={() => setActiveTab('notifications')}
              className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors relative ${!isOpen && 'justify-center'}`}
            >
              <Bell className="w-5 h-5" />
              {isOpen && <span className="text-sm">Notifications</span>}
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className={`w-full text-gray-300 hover:bg-gray-800 hover:text-white ${!isOpen && 'px-2'}`}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              {isOpen && <span className="ml-3">Logout</span>}
            </Button>
          </div>
        )}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
                </h2>
                <p className="text-sm text-gray-600">Welcome back, {user?.name}</p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Logged in as</p>
                  <p className="text-sm font-semibold text-gray-900">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message Banner */}
        {unreadMessageCount > 0 && activeTab !== 'messages' && (
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-6 shadow-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MessageSquare className="w-5 h-5" />
                <span className="font-medium">
                  You have {unreadMessageCount} unread message{unreadMessageCount !== 1 ? 's' : ''}
                </span>
              </div>
              <Button
                onClick={() => setActiveTab('messages')}
                size="sm"
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                View Messages
              </Button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 p-6 overflow-auto">
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fade-in">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="stat-centers">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm mb-1">Total Centers</p>
                      <p className="text-3xl font-bold">{stats.totalCenters}</p>
                    </div>
                    <MapPin className="w-12 h-12 text-blue-200 opacity-80" />
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="stat-teachers">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-cyan-100 text-sm mb-1">Total Teachers</p>
                      <p className="text-3xl font-bold">{stats.totalTeachers}</p>
                    </div>
                    <GraduationCap className="w-12 h-12 text-cyan-200 opacity-80" />
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="stat-students">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-100 text-sm mb-1">Total Students</p>
                      <p className="text-3xl font-bold">{stats.totalStudents}</p>
                    </div>
                    <Users className="w-12 h-12 text-purple-200 opacity-80" />
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="stat-reports">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm mb-1">Messages</p>
                      <p className="text-3xl font-bold">{unreadMessageCount}</p>
                    </div>
                    <MessageSquare className="w-12 h-12 text-purple-200 opacity-80" />
                  </div>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('messages')}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Send Message</h3>
                      <p className="text-sm text-gray-600">Broadcast to teachers</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('users')}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Manage Users</h3>
                      <p className="text-sm text-gray-600">Add or edit users</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('analytics')}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">View Analytics</h3>
                      <p className="text-sm text-gray-600">Performance insights</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Analytics Overview */}
              {analyticsOverview && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Performance Insights
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-0 hover:shadow-lg transition-all duration-300">
                      <p className="text-sm text-emerald-600 font-medium">Attendance Rate</p>
                      <h3 className="text-3xl font-bold text-emerald-900 mt-2">{analyticsOverview.attendance_rate}%</h3>
                      <p className="text-xs text-emerald-600 mt-1">Last 30 days</p>
                    </Card>

                    <Card className="p-6 bg-gradient-to-br from-amber-50 to-amber-100 border-0 hover:shadow-lg transition-all duration-300">
                      <p className="text-sm text-amber-600 font-medium">Avg Star Rating</p>
                      <h3 className="text-3xl font-bold text-amber-900 mt-2">{analyticsOverview.avg_star_rating}</h3>
                      <p className="text-xs text-amber-600 mt-1">Out of 5</p>
                    </Card>

                    <Card className="p-6 bg-gradient-to-br from-rose-50 to-rose-100 border-0 hover:shadow-lg transition-all duration-300">
                      <p className="text-sm text-rose-600 font-medium">Task Completion</p>
                      <h3 className="text-3xl font-bold text-rose-900 mt-2">{analyticsOverview.task_completion_rate}%</h3>
                      <p className="text-xs text-rose-600 mt-1">All tasks</p>
                    </Card>

                    <Card className="p-6 bg-gradient-to-br from-sky-50 to-sky-100 border-0 hover:shadow-lg transition-all duration-300">
                      <p className="text-sm text-sky-600 font-medium">Active Students</p>
                      <h3 className="text-3xl font-bold text-sky-900 mt-2">{analyticsOverview.active_students}</h3>
                      <p className="text-xs text-sky-600 mt-1">of {stats.totalStudents} total</p>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'ai-analytics' && <AIAnalyticsDashboard userRole="admin" />}
          {activeTab === 'messages' && <MessagingSystem userRole="admin" />}
          {activeTab === 'analytics' && <Analytics />}
          {activeTab === 'approvals' && <ApprovalsManagement />}
          {activeTab === 'finance' && <FinanceManagement />}
          {activeTab === 'games' && <AdminGameLeaderboard user={user} />}
          {activeTab === 'kit' && <KitRequestManagement />}
          {activeTab === 'centers' && <CenterManagement />}
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'notifications' && <Notifications notifications={notifications} onUpdate={loadNotifications} />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
